/**
 * 工作区详情页面组件
 *
 * 工作区核心页面，提供以下布局：
 * - 顶部栏：返回、创作树入口
 * - 左侧边栏：设定/章节导航切换
 * - 左侧列表区域：设定列表/章节目录树
 * - 中间详情区域：设定详情/章节编辑器
 * - 右侧面板：Chat/工作流/团队 Tab 切换
 *
 * 视图模式：
 * - main: 主工作区（设定/章节编辑）
 * - creativeTree: 创作树全屏视图
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FourPanelLayout } from '../components/layout/FourPanelLayout';
import { TopBar } from '../components/layout/TopBar';
import { RightPanel, RightPanelTab } from '../components/layout/RightPanel';
import { WorkspaceSidebar, PanelType } from '../components/workspace/WorkspaceSidebar';
import { WorkspaceOverview } from '../components/workspace/WorkspaceOverview';
import { SettingList } from '../components/setting/SettingList';
import { SettingDetail } from '../components/setting/SettingDetail';
import { CreateSettingModal } from '../components/setting/CreateSettingModal';
import { ChapterTree } from '../components/chapter/ChapterTree';
import { ChapterEditor } from '../components/chapter/ChapterEditor';
import { BranchTree } from '../components/branch';
import { ChatWindow } from '../components/chat/ChatWindow';
import { WorkflowEditor } from '../components/workflow/WorkflowEditor';
import { AgentTeamPanel } from '../components/agent-team/AgentTeamPanel';
import { MaterialList } from '../components/knowledge/MaterialList';
import { MaterialDetail } from '../components/knowledge/MaterialDetail';
import { DocumentUpload } from '../components/knowledge/DocumentUpload';
import type { DocumentUploadResponse } from '../types/document';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { NotFoundMessage } from '../components/common/NotFoundMessage';
import { EmptyState } from '../components/common/EmptyState';
import { TabbedDetailPanel, type Tab } from '../components/workspace/TabbedDetailPanel';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { useRefreshStore } from '../store/refreshStore';
import { useAutoTrackStore } from '../store/autoTrackStore';
import { ChapterService } from '../services/chapter';
import type { Setting } from '../types/setting';
import type { Chapter } from '../types/chapter';

// ==================== 类型定义 ====================

/** 主视图模式 */
type MainViewMode = 'main' | 'creativeTree';

// ==================== 常量定义 ====================

const MAX_WORKSPACE_ID = 2147483647;

// ==================== Helper 函数 ====================

/**
 * 脱敏错误信息，返回用户友好的错误提示
 */
function sanitizeErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) {
    return '加载工作区失败，请稍后重试';
  }
  const errorMsg = error.message.toLowerCase();
  if (errorMsg.includes('权限不足') || errorMsg.includes('未授权') || errorMsg.includes('unauthorized')) {
    return '您没有权限访问此工作区';
  }
  if (errorMsg.includes('不存在') || errorMsg.includes('not found') || errorMsg.includes('404')) {
    return '工作区不存在';
  }
  if (errorMsg.includes('网络') || errorMsg.includes('network') || errorMsg.includes('fetch')) {
    return '网络连接失败，请检查网络后重试';
  }
  return '加载工作区失败，请稍后重试';
}

// ==================== 主组件 ====================

/**
 * 工作区页面组件
 *
 * @returns {JSX.Element} 工作区页面组件
 */
export const WorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 解析 workspaceId，添加严格验证
  const workspaceId = useMemo(() => {
    if (!id) return 0;
    const parsedId = parseInt(id, 10);
    if (isNaN(parsedId) || parsedId <= 0) {
      return 0;
    }
    if (parsedId > MAX_WORKSPACE_ID) {
      return 0;
    }
    return parsedId;
  }, [id]);

  // Store hooks
  const { currentWorkspace, getWorkspace, loading, error } = useWorkspaceStore();
const { token, isAuthenticated, initialized, loading: authLoading } = useAuthStore();
const { triggerSettingsRefresh, triggerChaptersRefresh } = useRefreshStore();

// 主视图模式状态
  const [mainViewMode, setMainViewMode] = useState<MainViewMode>('main');

  // 左侧面板状态（概况/设定/章节）
  const [leftPanelType, setLeftPanelType] = useState<PanelType>('overview');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreateSettingModal, setShowCreateSettingModal] = useState(false);
  const [settings, setSettings] = useState<Setting[]>([]);
  
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [showMaterialUpload, setShowMaterialUpload] = useState(false);

  // 多标签页状态
  const [openTabs, setOpenTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

// 右侧面板 Tab 状态
const [rightPanelTab, setRightPanelTab] = useState<RightPanelTab>('chat');

// 中间栏折叠状态
  const [centerPanelCollapsed, setCenterPanelCollapsed] = useState(false);

  /**
   * 加载工作区数据
   */
  useEffect(() => {
    if (workspaceId && isAuthenticated) {
      const loadWorkspace = async () => {
        try {
          setLoadError(null);
          await getWorkspace(workspaceId);
        } catch (err) {
          setLoadError(sanitizeErrorMessage(err));
          console.error('[WorkspacePage] 加载工作区失败:', err);
        }
      };

      loadWorkspace();
    }
  }, [workspaceId, isAuthenticated, getWorkspace]);

/**
 * 验证 workspaceId 是否有效
 */
  useEffect(() => {
    if (!id && workspaceId === 0) {
      const timer = setTimeout(() => {
        if (!id || workspaceId === 0) {
          navigate('/', { replace: true });
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    if (workspaceId === 0 && id) {
      navigate('/', { replace: true });
    }
  }, [id, workspaceId, navigate]);

  /**
   * 切换左侧面板类型时，保留标签页状态
   * 让用户可以在不同面板间切换时不丢失已打开的内容
   */
  useEffect(() => {
    // 保留标签页状态，不清空
  }, [leftPanelType]);

  // ==================== 多标签页管理回调 ====================

  /**
   * 打开或切换到标签页
   * 当标签页已存在时，同步更新数据（用于响应广播刷新事件）
   */
  const openOrSwitchTab = useCallback((item: Chapter | Setting, type: 'chapter' | 'setting') => {
    const id = `${type}-${item.id}`;
    
    // 检查是否已打开
    const existingTab = openTabs.find(tab => tab.id === id);
    if (existingTab) {
      // 标签页已存在，更新数据并切换到该标签页
      setOpenTabs(prev => prev.map(tab => 
        tab.id === id ? { ...tab, data: item, title: item.name } : tab
      ));
      setActiveTabId(id);
    } else {
      // 新建标签
      const newTab: Tab = {
        id,
        type,
        title: item.name,
        data: item
      };
      setOpenTabs(prev => [...prev, newTab]);
      setActiveTabId(id);
    }
    // 确保中间面板展开
    setCenterPanelCollapsed(false);
  }, [openTabs]);

  /**
   * 切换标签页
   */
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  /**
   * 关闭标签页
   */
  const handleTabClose = useCallback((tabId: string) => {
    setOpenTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      
      // 如果关闭的是当前标签，切换到前一个或清空
      if (activeTabId === tabId) {
        const closedIndex = prev.findIndex(tab => tab.id === tabId);
        const newActiveTab = newTabs[Math.min(closedIndex, newTabs.length - 1)];
        setActiveTabId(newActiveTab?.id || null);
      }
      
      return newTabs;
    });
  }, [activeTabId]);

  /**
   * 关闭整个中间面板
   */
  const handlePanelClose = useCallback(() => {
    setCenterPanelCollapsed(true);
  }, []);

  /**
   * 更新标签页数据
   */
  const handleTabDataUpdate = useCallback((tabId: string, data: Chapter | Setting) => {
    setOpenTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, data, title: data.name } : tab
    ));
  }, []);

  // ==================== 回调函数 ====================

  /**
   * 返回作者中心
   */
  const handleBack = useCallback(() => {
    navigate('/author-center');
  }, [navigate]);

  /**
   * 打开创作树视图
   */
  const handleOpenCreativeTree = useCallback(() => {
    setMainViewMode('creativeTree');
  }, []);

  /**
   * 返回主视图
   */
  const handleBackToMain = useCallback(() => {
    setMainViewMode('main');
  }, []);

  /**
   * 选择设定的回调函数
   * 打开或切换到对应的标签页
   */
  const handleSelectSetting = useCallback((setting: Setting) => {
    openOrSwitchTab(setting, 'setting');
  }, [openOrSwitchTab]);

  /**
   * 选择章节的回调函数
   * 打开或切换到对应的标签页
   */
  const handleSelectChapter = useCallback((chapter: Chapter) => {
    openOrSwitchTab(chapter, 'chapter');
  }, [openOrSwitchTab]);

  /**
   * 处理创建设定按钮点击
   */
  const handleCreateSetting = useCallback(() => {
    setShowCreateSettingModal(true);
  }, []);

  /**
   * 处理设定列表变化
   */
  const handleSettingsChange = useCallback((newSettings: Setting[]) => {
    setSettings(newSettings);
  }, []);

  /**
   * 处理设定删除成功
   */
  const handleSettingDelete = useCallback((deletedSettingId: number) => {
    // 关闭对应标签页
    const tabId = `setting-${deletedSettingId}`;
    handleTabClose(tabId);
    setSettings(prevSettings =>
      prevSettings.filter(s => s.id !== deletedSettingId)
    );
  }, [handleTabClose]);

  /**
   * 处理设定创建成功
   */
  const handleSettingCreateSuccess = useCallback((setting: Setting) => {
    setShowCreateSettingModal(false);
    triggerSettingsRefresh();
    // 自动打开新创建的设定
    openOrSwitchTab(setting, 'setting');
  }, [triggerSettingsRefresh, openOrSwitchTab]);

  /**
   * 处理创建设定对话框关闭
   */
  const handleSettingCreateClose = useCallback(() => {
    setShowCreateSettingModal(false);
  }, []);

  /**
   * 处理创建章节按钮点击
   */
  const handleCreateChapter = useCallback(() => {
    // ChapterTree 组件内部会显示 CreateChapterDialog
  }, []);

  /**
   * 处理知识库向量条目选择
   */
  /**
   * 处理自动追踪事件
   */
  useEffect(() => {
    const handleSettingTrack = (e: CustomEvent<{ targetId: number; startOffset?: number; endOffset?: number }>) => {
      if (!useAutoTrackStore.getState().enabled) return;
      const { targetId } = e.detail;
      setMainViewMode('main');
      setLeftPanelType('setting');
      // 查找设定并打开标签页
      const targetSetting = settings.find(s => s.id === targetId);
      if (targetSetting) {
        openOrSwitchTab(targetSetting, 'setting');
      }
      useAutoTrackStore.getState().setTrackTarget('setting', targetId);
      useAutoTrackStore.getState().triggerHighlight(targetId, 'setting');
    };

    const handleChapterTrack = async (e: CustomEvent<{ targetId: number; startOffset?: number; endOffset?: number }>) => {
      if (!useAutoTrackStore.getState().enabled) return;
      const { targetId, startOffset, endOffset } = e.detail;
      setMainViewMode('main');
      setLeftPanelType('chapter');

      // 根据 targetId 查找正确的章节对象并打开标签页
      // targetId 可能是 chapter.id（数据库主键）或 chapter.num（章节序号）
      try {
        const chapterService = new ChapterService(undefined, token || undefined, workspaceId);
        const chapters = await chapterService.listChapters(workspaceId);
        const targetChapter = chapters.find(c => c.id === targetId || c.num === targetId);

        if (targetChapter) {
          openOrSwitchTab(targetChapter, 'chapter');
          console.log('[handleChapterTrack] Found and opened chapter:', targetChapter);
        } else {
          console.warn('[handleChapterTrack] Chapter not found for targetId:', targetId);
        }
      } catch (error) {
        console.error('[handleChapterTrack] Failed to load chapter:', error);
      }

      useAutoTrackStore.getState().setTrackTarget('chapter', targetId);
      useAutoTrackStore.getState().triggerHighlight(targetId, 'chapter', {
        startOffset,
        endOffset,
      });
    };

    window.addEventListener('autoTrack:setting', handleSettingTrack as unknown as EventListener);
    window.addEventListener('autoTrack:chapter', handleChapterTrack as unknown as EventListener);

    return () => {
      window.removeEventListener('autoTrack:setting', handleSettingTrack as unknown as EventListener);
      window.removeEventListener('autoTrack:chapter', handleChapterTrack as unknown as EventListener);
    };
  }, [settings, workspaceId, token, openOrSwitchTab]);

  /**
   * 重试加载工作区
   */
  const handleRetry = useCallback(() => {
    if (workspaceId && isAuthenticated) {
      getWorkspace(workspaceId);
    }
  }, [workspaceId, isAuthenticated, getWorkspace]);

  // ==================== 渲染函数 ====================

  /**
   * 渲染左侧面板内容
   */
  const renderLeftPanel = useCallback(() => {
    switch (leftPanelType) {
      case 'overview':
        return currentWorkspace ? (
          <WorkspaceOverview workspace={currentWorkspace} />
        ) : (
          <div className="h-full flex items-center justify-center">
            <LoadingSpinner message="加载概况中..." size="sm" />
          </div>
        );
      case 'setting':
        return (
          <SettingList
            workspaceId={workspaceId}
            selectedSettingId={openTabs.find(t => t.id === activeTabId && t.type === 'setting')?.data.id}
            onSettingSelect={handleSelectSetting}
            onSettingCreate={handleCreateSetting}
            onSettingsChange={handleSettingsChange}
            onSettingDelete={handleSettingDelete}
          />
        );
      case 'chapter':
        return (
          <ChapterTree
            workspaceId={workspaceId}
            selectedChapterId={openTabs.find(t => t.id === activeTabId && t.type === 'chapter')?.data.id}
            onChapterSelect={handleSelectChapter}
            onChapterCreate={handleCreateChapter}
          />
        );
      case 'material':
        return (
          <MaterialList
            workspaceId={workspaceId}
            onDocumentSelect={(doc) => {
              setSelectedMaterialId(doc.id);
              setShowMaterialUpload(false);
            }}
            className="h-full"
          />
        );
      default:
        return null;
    }
  }, [
    leftPanelType,
    workspaceId,
    currentWorkspace,
    openTabs,
    activeTabId,
    handleSelectSetting,
    handleSelectChapter,
    handleCreateSetting,
    handleCreateChapter,
    handleSettingsChange,
    handleSettingDelete,
    setSelectedMaterialId,
    setShowMaterialUpload,
  ]);

  useEffect(() => {
    const handleMaterialUpload = () => {
      setShowMaterialUpload(true);
      setSelectedMaterialId(null);
    };
    window.addEventListener('material:upload', handleMaterialUpload);
    return () => {
      window.removeEventListener('material:upload', handleMaterialUpload);
    };
  }, []);

  /**
   * 渲染章节内容
   */
  const renderChapterContent = useCallback((chapter: Chapter, onUpdate: (chapter: Chapter | null) => void) => {
    // 包装 onUpdate 以处理标签页更新
    const handleChapterUpdateWrapper = (updatedChapter: Chapter | null) => {
      if (updatedChapter === null) {
        // 章节被删除，关闭对应标签页
        const tabId = `chapter-${chapter.id}`;
        handleTabClose(tabId);
      } else {
        // 更新标签页数据
        const tabId = `chapter-${updatedChapter.id}`;
        handleTabDataUpdate(tabId, updatedChapter);
        triggerChaptersRefresh(updatedChapter.id);
      }
      // 调用原始的 onUpdate
      onUpdate(updatedChapter);
    };

    return (
      <ChapterEditor
        chapter={chapter}
        onChapterUpdate={handleChapterUpdateWrapper}
      />
    );
  }, [handleTabClose, handleTabDataUpdate, triggerChaptersRefresh]);

  /**
   * 渲染设定内容
   */
  const renderSettingContent = useCallback((setting: Setting, onUpdate: (setting: Setting | null) => void) => {
    // 包装 onUpdate 以处理标签页更新
    const handleSettingUpdateWrapper = (updatedSetting: Setting | null) => {
      if (updatedSetting === null) {
        // 设定被删除，关闭对应标签页
        const tabId = `setting-${setting.id}`;
        handleTabClose(tabId);
      } else {
        // 更新标签页数据
        const tabId = `setting-${updatedSetting.id}`;
        handleTabDataUpdate(tabId, updatedSetting);
        setSettings(prevSettings =>
          prevSettings.map(s => s.id === updatedSetting.id ? updatedSetting : s)
        );
      }
      // 调用原始的 onUpdate
      onUpdate(updatedSetting);
    };

    return (
      <SettingDetail
        workspaceId={workspaceId}
        setting={setting}
        onUpdate={handleSettingUpdateWrapper}
        onDelete={handleSettingDelete}
      />
    );
  }, [workspaceId, handleSettingDelete, handleTabClose, handleTabDataUpdate]);

  /**
   * 渲染中间面板内容
   */
  const renderCenterPanel = useCallback(() => {
    switch (leftPanelType) {
      case 'overview':
        // 概况视图：中间面板显示提示
        return (
          <div className="h-full flex flex-col items-center justify-center bg-white p-8">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4">📊</div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">作品概况</h2>
              <p className="text-gray-600 mb-4">
                在左侧面板中查看作品的详细数据和创作进度。
              </p>
              <p className="text-sm text-gray-500">
                包括封面、简介、字数统计、创作进度和 Token 消耗等信息。
              </p>
            </div>
          </div>
        );
      case 'material':
        if (selectedMaterialId) {
          return (
            <MaterialDetail
              workspaceId={workspaceId}
              documentId={selectedMaterialId}
              onClose={() => setSelectedMaterialId(null)}
              className="h-full"
            />
          );
        }
        return (
          <div className="h-full flex flex-col items-center justify-center bg-white p-8 relative">
            <div className="text-center max-w-md">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">工作区</h2>
              <p className="text-gray-600 mb-4">
                在左侧面板中选择素材查看详情。
              </p>
              <p className="text-sm text-gray-500">
                支持上传文档作为创作素材。
              </p>
            </div>
          </div>
        );
      case 'setting':
      case 'chapter':
        // 设定/章节视图：使用多标签页组件
        return (
          <TabbedDetailPanel
            tabs={openTabs}
            activeTabId={activeTabId}
            onTabChange={handleTabChange}
            onTabClose={handleTabClose}
            onPanelClose={handlePanelClose}
            onTabDataUpdate={handleTabDataUpdate}
            renderChapter={renderChapterContent}
            renderSetting={renderSettingContent}
          />
        );
      default:
        return null;
    }
}, [
    leftPanelType,
    workspaceId,
    openTabs,
    activeTabId,
    centerPanelCollapsed,
    handleTabChange,
    handleTabClose,
    handlePanelClose,
    handleTabDataUpdate,
    renderChapterContent,
    renderSettingContent,
    selectedMaterialId,
    setSelectedMaterialId,
  ]);

  /**
   * 渲染 Chat 面板
   */
  const renderChatPanel = useCallback(() => {
    // 等待认证初始化完成
    if (!initialized || authLoading) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <LoadingSpinner />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="请先登录以使用AI创作助手" />
        </div>
      );
    }

    if (!token) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="会话已过期，请重新登录" />
        </div>
      );
    }

    return (
      <ChatWindow
        workspaceId={workspaceId}
        token={token}
      />
    );
  }, [workspaceId, token, isAuthenticated, initialized, authLoading]);

/**
  * 渲染工作流面板
  */
  const renderWorkflowPanel = useCallback(() => {
    // 等待认证初始化完成
    if (!initialized || authLoading) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <LoadingSpinner />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="请先登录以使用工作流功能" />
        </div>
      );
    }

    if (!token) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="会话已过期，请重新登录" />
        </div>
      );
    }

    return (
      <WorkflowEditor
        workspaceId={workspaceId}
        showToolbar={true}
        showConfigPanel={true}
        showMiniMap={true}
        showControls={true}
        showBackground={true}
      />
    );
}, [isAuthenticated, token, workspaceId, initialized, authLoading]);

/**
  * 渲染团队面板
  */
  const renderAgentTeamPanel = useCallback(() => {
    // 等待认证初始化完成
    if (!initialized || authLoading) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <LoadingSpinner />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="请先登录以使用团队协作功能" />
        </div>
      );
    }

    if (!token) {
      return (
        <div className="h-full flex items-center justify-center bg-white">
          <EmptyState message="会话已过期，请重新登录" />
        </div>
      );
    }

    return (
      <AgentTeamPanel
        workspaceId={workspaceId}
      />
    );
  }, [isAuthenticated, workspaceId, initialized, authLoading]);

/**
  * 渲染右侧面板
    */
  const renderRightPanel = useCallback(() => {
    return (
      <RightPanel
        chatPanel={renderChatPanel()}
        workflowPanel={renderWorkflowPanel()}
        agentteamPanel={renderAgentTeamPanel()}
        defaultTab={rightPanelTab}
        onTabChange={setRightPanelTab}
      />
    );
  }, [renderChatPanel, renderWorkflowPanel, renderAgentTeamPanel, rightPanelTab]);

  // ==================== 创作树视图 ====================
  
  const renderCreativeTreeView = useCallback(() => {
    return (
      <div className="h-screen flex flex-col bg-white">
        <TopBar
          onBack={handleBack}
          onBackToMain={handleBackToMain}
          onCreativeTree={handleBackToMain}
          isCreativeTreeActive={true}
          workspaceName={currentWorkspace?.name}
        />
        <div className="flex-1 overflow-hidden">
          <BranchTree
            workspaceId={workspaceId}
            showToolbar={true}
          />
        </div>
      </div>
    );
  }, [handleBack, handleBackToMain, workspaceId, currentWorkspace?.name]);

  // ==================== 加载/错误状态 ====================

  // 加载中状态
  if (loading && !currentWorkspace) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner message="加载工作区中..." size="lg" />
      </div>
    );
  }

  // 错误状态
  if (error || loadError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <ErrorMessage
          message={error || loadError || '加载工作区失败'}
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // 工作区不存在
  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <NotFoundMessage
          title="工作区不存在"
          message="您访问的工作区不存在或已被删除"
        />
      </div>
    );
  }

  // ==================== 视图切换 ====================

  // 创作树视图
  if (mainViewMode === 'creativeTree') {
    return renderCreativeTreeView();
  }

  // 主工作区视图
  return (
    <>
      {leftPanelType === 'overview' ? (
        // 概况视图：全屏展示，占满整个内容区域
        <div className="h-screen flex flex-col bg-white">
          <TopBar
            onBack={handleBack}
            onCreativeTree={handleOpenCreativeTree}
            workspaceName={currentWorkspace?.name}
          />
          <div className="flex-1 flex overflow-hidden">
            {/* 侧边栏 */}
            <div className="w-16 flex-shrink-0">
              <WorkspaceSidebar
                currentType={leftPanelType}
                onTypeChange={setLeftPanelType}
              />
            </div>
            {/* 概况内容：占满剩余空间 */}
            <div className="flex-1 overflow-hidden">
              {currentWorkspace && (
                <WorkspaceOverview
                  workspace={currentWorkspace}
                  onWorkspaceUpdate={(updated) => {
                    // 更新当前工作区数据
                    useWorkspaceStore.setState({ currentWorkspace: updated });
                  }}
                />
              )}
            </div>
          </div>
        </div>
      ) : leftPanelType === 'material' ? (
        <FourPanelLayout
          topBar={
            <TopBar
              onBack={handleBack}
              onCreativeTree={handleOpenCreativeTree}
              workspaceName={currentWorkspace?.name}
            />
          }
          sidebar={
            <WorkspaceSidebar
              currentType={leftPanelType}
              onTypeChange={setLeftPanelType}
            />
          }
          leftPanel={renderLeftPanel()}
          centerPanel={renderCenterPanel()}
          rightPanel={renderRightPanel()}
          centerPanelCollapsed={centerPanelCollapsed}
          defaultWidths={{
            leftPanel: 280,
            centerPanel: 'auto',
            rightPanel: 400,
          }}
        />
      ) : (
        // 设定/章节视图：使用四栏布局
        <FourPanelLayout
          topBar={
            <TopBar
              onBack={handleBack}
              onCreativeTree={handleOpenCreativeTree}
              workspaceName={currentWorkspace?.name}
            />
          }
          sidebar={
            <WorkspaceSidebar
              currentType={leftPanelType}
              onTypeChange={setLeftPanelType}
            />
          }
          leftPanel={renderLeftPanel()}
          centerPanel={renderCenterPanel()}
          rightPanel={renderRightPanel()}
          centerPanelCollapsed={centerPanelCollapsed}
          defaultWidths={{
            leftPanel: 280,
            centerPanel: 'auto',
            rightPanel: 400,
          }}
        />
      )}

      {/* 创建设定对话框 */}
      <CreateSettingModal
        workspaceId={workspaceId}
        isOpen={showCreateSettingModal}
        onClose={handleSettingCreateClose}
        onSuccess={handleSettingCreateSuccess}
        existingSettings={settings}
      />

      {/* 上传素材对话框 */}
      {showMaterialUpload && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowMaterialUpload(false)}
        >
          <div
            className="bg-white rounded-lg shadow-md w-full max-w-2xl max-h-[80vh] mx-4 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">上传素材</h3>
              <button
                onClick={() => setShowMaterialUpload(false)}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <DocumentUpload
                workspaceId={workspaceId}
                onUploadComplete={(response: DocumentUploadResponse) => {
                  setShowMaterialUpload(false);
                  setSelectedMaterialId(response.id);
                }}
                className="h-full"
                autoUpload={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkspacePage;
